import { Box, Snackbar, Alert } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { useTranslate } from "src/locales/use-locales";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import { useEffect, useState } from "react";
import ByzMcParameterDetailSelect from "src/components/shared-components/ByzMcParameterDetailSelect";
import { McParameterDetailResponseDto } from "src/api/references/referenceServicesDtos";
import { useAppSelector } from "src/app/hooks";
import { OtherPrivilegeGuard } from "src/utils/authorization/OtherPrivilegeGuard";
import { useCardForm } from "src/hooks/useCardForm";

interface ModifyAldetreFormData {
  MS_ID: string;
  CTIME: Date;
  PROCESS_TIME: Date;
  CUSER: string;
  LEVEL_CONSCIOUSNESS: string;
  ACTIVITY: string;
  RESPIRATORY: string;
  SPO2: string;
  BLOOD_PRESSURE: string;
  PULSE: string;
  LEVEL_CONSCIOUSNESS_XVALUE: string;
  ACTIVITY_XVALUE: string;
  RESPIRATORY_XVALUE: string;
  SPO2_XVALUE: string;
  BLOOD_PRESSURE_XVALUE: string;
  PULSE_XVALUE: string;
  MAS_SCORE: string;
}

interface AldetreScoreProps {
  recordId?: string;
  patientId: number;
  onClose?: () => void;
  getComId: () => Promise<number | null>;
  comId: number;
  setComId: (comId: number) => void;
  getCardTableData: (comId: number) => Promise<string | "">;
  createTableMasterId: () => Promise<string | null>;
  save: (id?: string) => Promise<void | string | null>;
  handleChange: (field: keyof ModifyAldetreFormData, value: any) => void;
  isSaving?: boolean;
  extraAccess?: boolean;
}

function ModifyAldetreCreate({
  recordId,
  save,
  handleChange: parentHandleChange,
  isSaving,
  extraAccess,
}: AldetreScoreProps) {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => state.auth);

  const {
    formData,
    selectedMCPs,
    loading,
    handleChange,
    handleMCPChange,
    resetForm,
    cardData,
    isEditMode,
    error,
    setError,
    getMCPParameterDetails,
    mcpCacheLoaded,
  } = useCardForm({
    cardType: "ALDETRE_SCORE",
    recordId,
    user,
    onLoadRecord: (record) => {
      Object.entries(record).forEach(([key, value]) => {
        parentHandleChange(key as keyof ModifyAldetreFormData, value);
      });
    },
  });

  const typedFormData = formData as ModifyAldetreFormData;

  useEffect(() => {
    if (!isEditMode) {
      resetForm();
    }
  }, [isEditMode, resetForm]);

  useEffect(() => {
    Object.entries(formData).forEach(([key, value]) => {
      parentHandleChange(key as keyof ModifyAldetreFormData, value);
    });
  }, [formData, parentHandleChange]);

  const [score, setScore] = useState(0);

  useEffect(() => {
    if (!typedFormData) return;

    const scoreFields = [
      "LEVEL_CONSCIOUSNESS_XVALUE",
      "ACTIVITY_XVALUE",
      "RESPIRATORY_XVALUE",
      "SPO2_XVALUE",
      "BLOOD_PRESSURE_XVALUE",
      "PULSE_XVALUE",
    ];

    const score = scoreFields.reduce((sum, field) => {
      const value = Number((typedFormData as any)[field]) || 0;
      return sum + value;
    }, 0);

    setScore(score);
    handleChange("MAS_SCORE", score);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    typedFormData?.LEVEL_CONSCIOUSNESS_XVALUE,
    typedFormData?.ACTIVITY_XVALUE,
    typedFormData?.RESPIRATORY_XVALUE,
    typedFormData?.SPO2_XVALUE,
    typedFormData?.BLOOD_PRESSURE_XVALUE,
    typedFormData?.PULSE_XVALUE,
  ]);


  return (
    <>
      <Snackbar
        open={!!error?.open}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setError(null)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error?.message}
        </Alert>
      </Snackbar>

      <Box sx={{ padding: "20px", width: "100%" }}>
        <Box mb={3}>
          <OtherPrivilegeGuard
            extraAccess={!!extraAccess}
            fallback={
              <ByzMuiSaveButton
                disabled
                tooltip={!extraAccess ? t("no-permission-for-this-action") : undefined}
              />
            }
          >
            <ByzMuiSaveButton
              onClick={() => save(typedFormData.MS_ID)}
              loading={isSaving}
            />
          </OtherPrivilegeGuard>
        </Box>

        <Grid container spacing={2}>
          <Grid
            xs={12}
            sm={6}
            md={6}
            container
            direction={"column"}
            spacing={3}
          >
            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("modified-aldrete.title")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("datetime")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiDateTimePicker
                  sx={{ width: "100%" }}
                  value={typedFormData.PROCESS_TIME}
                  onChange={(date) => handleChange("PROCESS_TIME", date)}
                  disabled={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("createdby")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  label={t("createdby")}
                  variant="outlined"
                  value={typedFormData.CUSER}
                  onChange={(e) => handleChange("CUSER", e.target.value)}
                  disabled
                />
              </Grid>
            </Grid>

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("modified-aldrete.parameters")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("modified-aldrete.level_consciousness")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("modified-aldrete.level_consciousness")}
                  mcpId={cardData.mcpConfig?.LEVEL_CONSCIOUSNESS?.mcpId || 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("LEVEL_CONSCIOUSNESS", selected);
                    }
                  }}
                  value={selectedMCPs.LEVEL_CONSCIOUSNESS}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig?.LEVEL_CONSCIOUSNESS?.mcpId || 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("modified-aldrete.activity")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("modified-aldrete.activity")}
                  mcpId={cardData.mcpConfig?.ACTIVITY?.mcpId || 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("ACTIVITY", selected);
                    }
                  }}
                  value={selectedMCPs.ACTIVITY}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig?.ACTIVITY?.mcpId || 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("modified-aldrete.respiratory")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("modified-aldrete.respiratory")}
                  mcpId={cardData.mcpConfig?.RESPIRATORY?.mcpId || 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("RESPIRATORY", selected);
                    }
                  }}
                  value={selectedMCPs.RESPIRATORY}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig?.RESPIRATORY?.mcpId || 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("modified-aldrete.spo2")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("modified-aldrete.spo2")}
                  mcpId={cardData.mcpConfig?.SPO2?.mcpId || 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("SPO2", selected);
                    }
                  }}
                  value={selectedMCPs.SPO2}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig?.SPO2?.mcpId || 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("modified-aldrete.blood_pressure")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("modified-aldrete.blood_pressure")}
                  mcpId={cardData.mcpConfig?.BLOOD_PRESSURE?.mcpId || 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("BLOOD_PRESSURE", selected);
                    }
                  }}
                  value={selectedMCPs.BLOOD_PRESSURE}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig?.BLOOD_PRESSURE?.mcpId || 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("modified-aldrete.pulse")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMcParameterDetailSelect
                  title={t("modified-aldrete.pulse")}
                  mcpId={cardData.mcpConfig?.PULSE?.mcpId || 0}
                  multiple={false}
                  handleChange={(val) => {
                    if (val && !Array.isArray(val)) {
                      const selected = val as McParameterDetailResponseDto;
                      handleMCPChange("PULSE", selected);
                    }
                  }}
                  value={selectedMCPs.PULSE}
                  sx={{ width: "100%" }}
                  cachedData={getMCPParameterDetails(
                    cardData.mcpConfig?.PULSE?.mcpId || 0
                  )}
                  cacheLoaded={mcpCacheLoaded}
                  readOnly={!extraAccess}
                />
              </Grid>
            </Grid>

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle={true}>
                {t("modified-aldrete.other")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("modified-aldrete.mas-score")}
                </ByzMuiTypography>
              </Grid>
              <Grid md={6} xs={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  variant="outlined"
                  value={score}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default ModifyAldetreCreate;
